import React, { useState, useEffect } from 'react'
import { supabase } from '../supabase'

const CHANNELS = ['Instagram','Facebook','Yelp','Google','Nextdoor','Flyer','Referral','TikTok','Other']
const STATUSES = ['Planeada','Activa','Pausada','Completada']
const STATUS_COLORS = { Planeada:'#185fa5', Activa:'#1d9e75', Pausada:'#f59e0b', Completada:'#80868b' }

export default function Marketing() {
  const [campaigns, setCampaigns] = useState([])
  const [selected, setSelected] = useState(null)
  const [showAdd, setShowAdd] = useState(false)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [newUpdate, setNewUpdate] = useState('')
  const [clients, setClients] = useState([])

  const emptyForm = { name:'', channel:'Instagram', status:'Planeada', start_date:'', end_date:'', budget:'', notes:'', updates:[] }
  const [form, setForm] = useState(emptyForm)

  useEffect(() => { fetchAll() }, [])

  async function fetchAll() {
    setLoading(true)
    const [{ data: camp }, { data: cl }] = await Promise.all([
      supabase.from('marketing_campaigns').select('*').order('start_date', { ascending: false }),
      supabase.from('clients').select('id,name,source,date_started').eq('is_active',true),
    ])
    setCampaigns(camp||[])
    setClients(cl||[])
    setLoading(false)
  }

  async function saveCampaign() {
    setSaving(true)
    const payload = {
      name: form.name, channel: form.channel, status: form.status,
      start_date: form.start_date||null, end_date: form.end_date||null,
      budget: parseFloat(form.budget)||0, notes: form.notes,
    }
    if (selected) {
      await supabase.from('marketing_campaigns').update(payload).eq('id', selected.id)
      setCampaigns(prev=>prev.map(c=>c.id===selected.id?{...c,...payload}:c))
    } else {
      const { data } = await supabase.from('marketing_campaigns').insert([payload]).select().single()
      if (data) setCampaigns(prev=>[data,...prev])
    }
    setSaving(false)
    setShowAdd(false)
    setSelected(null)
  }

  async function addUpdate(campaign) {
    if (!newUpdate.trim()) return
    const today = new Date().toISOString().slice(0,10)
    const updates = [...(campaign.updates||[]), { date: today, note: newUpdate.trim() }]
    await supabase.from('marketing_campaigns').update({ updates }).eq('id', campaign.id)
    setCampaigns(prev=>prev.map(c=>c.id===campaign.id?{...c,updates}:c))
    setSelected(prev=>({...prev,updates}))
    setNewUpdate('')
  }

  async function deleteCampaign(id) {
    if (!window.confirm('¿Eliminar esta campaña?')) return
    await supabase.from('marketing_campaigns').delete().eq('id', id)
    setCampaigns(prev=>prev.filter(c=>c.id!==id))
    setSelected(null)
  }

  // Stats
  const activeCampaigns = campaigns.filter(c=>c.status==='Activa')
  const totalBudget = campaigns.reduce((s,c)=>s+parseFloat(c.budget||0),0)
  const activeBudget = activeCampaigns.reduce((s,c)=>s+parseFloat(c.budget||0),0)

  // Client source breakdown
  const byChannel = {}
  CHANNELS.forEach(ch=>{ byChannel[ch]=0 })
  clients.forEach(c=>{ const ch=c.source; if(ch) byChannel[ch]=(byChannel[ch]||0)+1 })
  const totalTracked = clients.filter(c=>c.source).length

  const sel = selected && campaigns.find(c=>c.id===selected.id) || selected

  return (
    <div style={s.wrap}>
      <div style={s.topbar}>
        <div style={{fontSize:15,fontWeight:600}}>📣 Marketing</div>
        <div style={{marginLeft:'auto'}}>
          <button style={s.addBtn} onClick={()=>{ setForm(emptyForm); setShowAdd(true); setSelected(null) }}>+ Nueva campaña</button>
        </div>
      </div>

      <div style={s.body}>
        {/* Summary stats */}
        <div style={s.statGrid}>
          <div style={s.statCard}><div style={s.statNum}>{campaigns.length}</div><div style={s.statLbl}>Campañas totales</div></div>
          <div style={s.statCard}><div style={{...s.statNum,color:'#1d9e75'}}>{activeCampaigns.length}</div><div style={s.statLbl}>Activas</div></div>
          <div style={s.statCard}><div style={s.statNum}>${totalBudget.toLocaleString()}</div><div style={s.statLbl}>Presupuesto total</div></div>
          <div style={s.statCard}><div style={{...s.statNum,color:'#e8547a'}}>${activeBudget.toLocaleString()}</div><div style={s.statLbl}>Presupuesto activo</div></div>
          <div style={s.statCard}><div style={s.statNum}>{totalTracked}</div><div style={s.statLbl}>Clientes con fuente</div></div>
        </div>

        <div style={s.mainGrid}>
          {/* Campaign list */}
          <div style={s.campList}>
            <div style={s.listHead}>Campañas</div>
            {loading ? <div style={s.loading}>Cargando...</div> :
              campaigns.length===0 ? <div style={s.empty}>Sin campañas aún</div> :
              campaigns.map(c=>(
                <div key={c.id} style={{...s.campRow,...(sel?.id===c.id?s.campRowOn:{})}}
                  onClick={()=>setSelected(c)}>
                  <div style={{...s.statusDot,background:STATUS_COLORS[c.status]||'#9aa0a6'}}/>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:12,fontWeight:500,color:'#202124',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{c.name}</div>
                    <div style={{fontSize:10,color:'#9aa0a6'}}>{c.channel} · {c.status}</div>
                  </div>
                  <div style={{fontSize:11,color:'#5f6368',flexShrink:0}}>${parseFloat(c.budget||0).toLocaleString()}</div>
                </div>
              ))
            }

            {/* Source breakdown */}
            <div style={{padding:'12px 10px',borderTop:'1px solid #e3e5e8',marginTop:8}}>
              <div style={{fontSize:10,fontWeight:600,textTransform:'uppercase',letterSpacing:'.05em',color:'#9aa0a6',marginBottom:8}}>Clientes por fuente</div>
              {Object.entries(byChannel).filter(([,v])=>v>0).sort((a,b)=>b[1]-a[1]).map(([ch,ct])=>(
                <div key={ch} style={{display:'flex',alignItems:'center',gap:6,marginBottom:5}}>
                  <div style={{fontSize:11,flex:1,color:'#5f6368'}}>{ch}</div>
                  <div style={{height:6,width:`${totalTracked>0?(ct/totalTracked)*80:0}px`,background:'#e8547a',borderRadius:3,minWidth:2}}/>
                  <div style={{fontSize:10,color:'#80868b',width:16,textAlign:'right'}}>{ct}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Campaign detail */}
          {sel ? (
            <div style={s.campDetail}>
              <div style={s.detailHead}>
                <div>
                  <div style={{fontSize:16,fontWeight:600,color:'#202124'}}>{sel.name}</div>
                  <div style={{display:'flex',gap:6,marginTop:4}}>
                    <span style={{...s.badge,background:STATUS_COLORS[sel.status]+'22',color:STATUS_COLORS[sel.status]}}>{sel.status}</span>
                    <span style={{...s.badge,background:'#f8f9fa',color:'#5f6368'}}>{sel.channel}</span>
                  </div>
                </div>
                <div style={{display:'flex',gap:6}}>
                  <button style={s.editBtn} onClick={()=>{ setForm({...sel,budget:sel.budget||''}); setShowAdd(true) }}>Editar</button>
                  <button style={{...s.editBtn,color:'#e24b4a'}} onClick={()=>deleteCampaign(sel.id)}>Eliminar</button>
                </div>
              </div>

              {/* Budget breakdown */}
              <div style={s.infoGrid}>
                <div style={s.infoCard}>
                  <div style={s.infoLabel}>Presupuesto</div>
                  <div style={s.infoVal}>${parseFloat(sel.budget||0).toLocaleString()}</div>
                </div>
                <div style={s.infoCard}>
                  <div style={s.infoLabel}>Inicio</div>
                  <div style={s.infoVal}>{sel.start_date ? new Date(sel.start_date+'T12:00:00').toLocaleDateString('es-MX',{month:'short',day:'numeric',year:'numeric'}) : '—'}</div>
                </div>
                <div style={s.infoCard}>
                  <div style={s.infoLabel}>Fin</div>
                  <div style={s.infoVal}>{sel.end_date ? new Date(sel.end_date+'T12:00:00').toLocaleDateString('es-MX',{month:'short',day:'numeric',year:'numeric'}) : '—'}</div>
                </div>
                <div style={s.infoCard}>
                  <div style={s.infoLabel}>Clientes ganados</div>
                  <div style={s.infoVal}>{clients.filter(c=>c.source===sel.channel.toLowerCase()).length}</div>
                </div>
              </div>

              {sel.notes && (
                <div style={{padding:'10px 12px',background:'#f8f9fa',borderRadius:8,fontSize:12,color:'#5f6368',marginBottom:12,lineHeight:1.6}}>
                  {sel.notes}
                </div>
              )}

              {/* Daily updates log */}
              <div style={s.updatesSection}>
                <div style={s.updatesTitle}>📅 Actualizaciones / notas del día</div>
                <div style={{display:'flex',gap:6,marginBottom:10}}>
                  <input style={s.updateInput} value={newUpdate} placeholder="Escribe una actualización..."
                    onChange={e=>setNewUpdate(e.target.value)}
                    onKeyDown={e=>e.key==='Enter'&&addUpdate(sel)}/>
                  <button style={s.updateBtn} onClick={()=>addUpdate(sel)}>+ Agregar</button>
                </div>
                <div style={s.updateList}>
                  {(sel.updates||[]).length===0 ? <div style={{fontSize:11,color:'#9aa0a6'}}>Sin actualizaciones aún</div> :
                    [...(sel.updates||[])].reverse().map((u,i)=>(
                      <div key={i} style={s.updateRow}>
                        <div style={s.updateDate}>{new Date(u.date+'T12:00:00').toLocaleDateString('es-MX',{month:'short',day:'numeric'})}</div>
                        <div style={{fontSize:12,color:'#444',flex:1}}>{u.note}</div>
                      </div>
                    ))
                  }
                </div>
              </div>
            </div>
          ) : (
            <div style={{...s.campDetail,display:'flex',alignItems:'center',justifyContent:'center',color:'#9aa0a6',flexDirection:'column',gap:8}}>
              <div style={{fontSize:32}}>📣</div>
              <div>Selecciona una campaña para ver detalles</div>
            </div>
          )}
        </div>
      </div>

      {/* ADD/EDIT MODAL */}
      {showAdd && (
        <div style={s.overlay} onClick={()=>setShowAdd(false)}>
          <div style={s.modal} onClick={e=>e.stopPropagation()}>
            <div style={s.modalTitle}>{selected?'Editar campaña':'Nueva campaña'}</div>
            {[['name','Nombre de campaña','text'],['budget','Presupuesto ($)','number'],['start_date','Fecha inicio','date'],['end_date','Fecha fin','date']].map(([k,l,t])=>(
              <div key={k} style={s.mfield}>
                <label style={s.mlabel}>{l}</label>
                <input type={t} style={s.minput} value={form[k]||''} onChange={e=>setForm(f=>({...f,[k]:e.target.value}))}/>
              </div>
            ))}
            <div style={s.mfield}>
              <label style={s.mlabel}>Canal</label>
              <select style={s.minput} value={form.channel} onChange={e=>setForm(f=>({...f,channel:e.target.value}))}>
                {CHANNELS.map(ch=><option key={ch}>{ch}</option>)}
              </select>
            </div>
            <div style={s.mfield}>
              <label style={s.mlabel}>Estado</label>
              <select style={s.minput} value={form.status} onChange={e=>setForm(f=>({...f,status:e.target.value}))}>
                {STATUSES.map(st=><option key={st}>{st}</option>)}
              </select>
            </div>
            <div style={s.mfield}>
              <label style={s.mlabel}>Notas</label>
              <textarea style={{...s.minput,height:70,resize:'vertical'}} value={form.notes||''} onChange={e=>setForm(f=>({...f,notes:e.target.value}))}/>
            </div>
            <div style={{display:'flex',gap:8}}>
              <button style={s.mCancel} onClick={()=>setShowAdd(false)}>Cancelar</button>
              <button style={s.mSave} onClick={saveCampaign} disabled={saving||!form.name}>{saving?'Guardando...':'Guardar'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

const s = {
  wrap:{display:'flex',flexDirection:'column',height:'100%',overflow:'hidden'},
  topbar:{display:'flex',alignItems:'center',gap:8,padding:'12px 16px',borderBottom:'1px solid #e3e5e8',background:'#fff',flexShrink:0},
  addBtn:{fontSize:12,padding:'6px 14px',border:'none',borderRadius:8,background:'#e8547a',color:'#fff',fontWeight:500,cursor:'pointer'},
  body:{flex:1,overflowY:'auto',padding:'14px 16px'},
  statGrid:{display:'grid',gridTemplateColumns:'repeat(5,1fr)',gap:8,marginBottom:14},
  statCard:{background:'#fff',borderRadius:10,padding:'10px 12px',border:'1px solid #e3e5e8'},
  statNum:{fontSize:20,fontWeight:600,color:'#202124'},
  statLbl:{fontSize:10,color:'#80868b',marginTop:2},
  mainGrid:{display:'grid',gridTemplateColumns:'220px 1fr',gap:12},
  campList:{background:'#fff',borderRadius:12,border:'1px solid #e3e5e8',overflow:'hidden'},
  listHead:{padding:'10px 12px',fontSize:11,fontWeight:600,textTransform:'uppercase',letterSpacing:'.05em',color:'#9aa0a6',borderBottom:'1px solid #e3e5e8'},
  campRow:{display:'flex',alignItems:'center',gap:8,padding:'9px 12px',borderBottom:'1px solid #f8f9fa',cursor:'pointer'},
  campRowOn:{background:'#fdeef3'},
  statusDot:{width:8,height:8,borderRadius:'50%',flexShrink:0},
  loading:{padding:16,fontSize:12,color:'#9aa0a6',textAlign:'center'},
  empty:{padding:16,fontSize:12,color:'#9aa0a6',textAlign:'center'},
  campDetail:{background:'#fff',borderRadius:12,border:'1px solid #e3e5e8',padding:'16px'},
  detailHead:{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:14},
  badge:{fontSize:11,padding:'3px 8px',borderRadius:20,fontWeight:500},
  editBtn:{fontSize:11,padding:'4px 10px',border:'1px solid #e3e5e8',borderRadius:6,background:'#fff',cursor:'pointer',color:'#5f6368'},
  infoGrid:{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:8,marginBottom:14},
  infoCard:{background:'#f8f9fa',borderRadius:8,padding:'8px 10px'},
  infoLabel:{fontSize:10,color:'#9aa0a6',marginBottom:2},
  infoVal:{fontSize:13,fontWeight:600,color:'#202124'},
  updatesSection:{background:'#f8f9fa',borderRadius:10,padding:'12px'},
  updatesTitle:{fontSize:12,fontWeight:600,color:'#333',marginBottom:8},
  updateInput:{flex:1,padding:'7px 9px',border:'1px solid #e3e5e8',borderRadius:8,fontSize:12,background:'#fff'},
  updateBtn:{padding:'7px 12px',border:'none',borderRadius:8,background:'#e8547a',color:'#fff',fontSize:12,cursor:'pointer',whiteSpace:'nowrap'},
  updateList:{display:'flex',flexDirection:'column',gap:6},
  updateRow:{display:'flex',gap:10,alignItems:'flex-start'},
  updateDate:{fontSize:10,color:'#9aa0a6',whiteSpace:'nowrap',paddingTop:2,minWidth:50},
  overlay:{position:'fixed',inset:0,background:'rgba(0,0,0,0.3)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:100},
  modal:{background:'#fff',borderRadius:14,padding:'20px 22px',width:360,maxHeight:'85vh',overflowY:'auto',boxShadow:'0 8px 32px rgba(0,0,0,0.15)'},
  modalTitle:{fontSize:15,fontWeight:600,color:'#202124',marginBottom:14},
  mfield:{marginBottom:12},
  mlabel:{fontSize:11,color:'#80868b',display:'block',marginBottom:3},
  minput:{width:'100%',padding:'7px 9px',border:'1px solid #e3e5e8',borderRadius:8,fontSize:13,background:'#f8f9fa',color:'#202124'},
  mCancel:{flex:1,padding:'8px',border:'1px solid #e3e5e8',borderRadius:8,background:'#fff',color:'#80868b',cursor:'pointer',fontSize:12},
  mSave:{flex:2,padding:'8px',border:'none',borderRadius:8,background:'#e8547a',color:'#fff',fontWeight:500,cursor:'pointer',fontSize:12},
}
